<template>
  <div class="approve-page gm-page pa-4 d-flex flex-column min-h-0">
    <ApproveHeader :station="stationHeader" :is-step-editing="isStepEditing" :saving="savingEdit"
      :can-approve="canApprove" :can-edit="canEdit" :can-resubmit="canResubmit" :resubmitting="resubmitting"
      @reject="openRejectDialog" @approve="openApproveDialog" @start-edit="startEdit" @cancel-edit="cancelEdit"
      @save-edit="saveEdit" @resubmit="onResubmit" />

    <ApproveRejectDialog v-model="rejectDialogVisible" :submitting="rejectSubmitting" @confirm="onRejectConfirm" />

    <ApproveConfirmDialog v-model="approveDialogVisible" :station-name="stationHeader.name"
      :submitting="approveSubmitting" @confirm="onApproveConfirm" />

    <section class="approve-body gm-card rounded-xl mt-4 flex-grow-1 min-h-0">
      <div class="approve-frame flex-grow-1 min-h-0">
        <aside class="approve-sidebar">
          <p class="approve-sidebar__hint text-body-2 mb-4">
            请对以下事项进行最终检查与确认，确认无误后即可投运：
          </p>
          <WizardStepper v-model="currentStepIndex" variant="sidebar" :steps="wizardSteps"
            class="approve-sidebar__stepper" />
        </aside>

        <div class="approve-mobile-head d-flex d-lg-none flex-column ga-3">
          <span class="approve-mobile-head__steps text-caption font-weight-semibold text-medium-emphasis">
            步骤 {{ currentStepIndex + 1 }} / {{ wizardSteps.length }}
          </span>
          <div class="approve-mobile-progress" aria-hidden="true">
            <div class="approve-mobile-progress__fill" :style="{ width: mobileProgressPct + '%' }" />
          </div>
          <WizardStepper v-model="currentStepIndex" inline :steps="wizardSteps" />
        </div>

        <main class="approve-main min-h-0">
          <div class="approve-step-body" :class="{
            'approve-step-body--readonly': !isStepEditing,
            'approve-step-body--device': activeStep === 'device',
            'approve-step-body--topo': activeStep === 'topo',
          }">
            <StationAddStepBasic v-if="activeStep === 'basic'" :read-only="!isStepEditing" />
            <StationAddStepDevice v-else-if="activeStep === 'device'" ref="deviceStepRef" :read-only="!isStepEditing"
              :station-id="stationHeader.id" />
            <StationAddStepTopology v-else-if="activeStep === 'topo'" :key="`topo-${isStepEditing}`"
              ref="topologyStepRef" :read-only="!isStepEditing" @update:draft="stationTopologyDraft = $event" />
          </div>
        </main>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { eui } from 'ems-ui'
import dayjs from 'dayjs'
import ApproveHeader from './components/ApproveHeader.vue'
import ApproveRejectDialog from './components/ApproveRejectDialog.vue'
import ApproveConfirmDialog from './components/ApproveConfirmDialog.vue'
import WizardStepper from '@/views/it-operations/station-add/components/WizardStepper.vue'
import StationAddStepBasic from '@/views/it-operations/station-add/components/StationAddStepBasic.vue'
import StationAddStepDevice from '@/views/it-operations/station-add/components/StationAddStepDevice.vue'
import StationAddStepTopology from '@/views/it-operations/station-add/components/StationAddStepTopology.vue'
import { useStationAddBasic } from '@/views/it-operations/station-add/useStationAddBasic'
import { approveChecklistVisible } from './useApproveData'
import {
  detail as getStationDetail,
  getStationApprovalList,
  update as updateStation,
  approveStationApproval,
  submitStationApproval,
} from '@/api/device/station'
import { getTopology, postTopology } from '@/api/device/topo'
import { checkRole } from '@/utils/permission.js'
import { parseDeviceFlags } from '@/utils/deviceFlag'

const route = useRoute()
const router = useRouter()

const activeStep = ref('basic')
const editingStepKey = ref(null)
const stationTopologyDraft = ref(null)
const topologyStepRef = ref(null)
const deviceStepRef = ref(null)
const rejectDialogVisible = ref(false)
const rejectSubmitting = ref(false)
const approveDialogVisible = ref(false)
const approveSubmitting = ref(false)
const resubmitting = ref(false)

/** role=creator 时代表申请者视角进入详情，不显示「驳回 / 投运」按钮；
 *  已通过 (status=1) / 已驳回 (status=2) 均为终态，也不再显示 */
const canApprove = computed(() => {
  if (route.query.role === 'creator') return false
  const status = approvalLatest.value?.status
  if (status === 1 || status === 2) return false
  return true
})

/** 「提交」按钮：仅申请者视角且当前电站已驳回时展示，用于重新发起审批 */
const canResubmit = computed(
  () => route.query.role === 'creator' && (approvalLatest.value == null || approvalLatest.value?.status === 2),
)

const basicState = useStationAddBasic()
const {
  form,
  validate,
  buildBasicPayload,
  resetForm,
  getPhotoUrl,
  setPhotoSlotFromStationPicture,
  normalizeAddressDetail,
} = basicState

// 详情接口返回的 strategyConfig,保存时按需合并回传
const existedStrategyConfig = ref(null)
// 详情接口返回的 configStep,保存时用 Math.max 单调推进,避免因编辑倒退流程
const serverConfigStep = ref(0)
const savingEdit = ref(false)

const stepKeys = approveChecklistVisible.map((i) => i.key)

const wizardSteps = approveChecklistVisible.map((item) => ({
  key: item.key,
  title: item.title,
  desc: item.hint,
  activeStatus: '已查阅',
}))

const currentStepIndex = computed({
  get() {
    const idx = stepKeys.indexOf(activeStep.value)
    return idx >= 0 ? idx : 0
  },
  set(idx) {
    const key = stepKeys[idx]
    if (key) activeStep.value = key
  },
})

const mobileProgressPct = computed(
  () => ((currentStepIndex.value + 1) / wizardSteps.length) * 100,
)

const isStepEditing = computed(() => editingStepKey.value === activeStep.value)

/** 后端时间统一格式化为 YYYY-MM-DD HH:mm:ss */
const fmtTime = (v) => {
  if (!v) return ''
  const d = dayjs(v)
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : String(v)
}

/** 展示型号 / 容量：kW/kWh 拼接 */
const composeCapacity = (row = {}) => {
  const power = row.maxPower ? `${row.maxPower}kW` : ''
  const cap = row.capacity ? `${row.capacity}kWh` : ''
  if (power && cap) return `${power}/${cap}`
  return power || cap || ''
}

const APPROVE_STATUS_TEXT = { 0: '审批中', 1: '已通过', 2: '已驳回' }

/** 电站接口原始数据 & 最新一条审批记录，供 Header 展示使用 */
const stationRaw = ref({})
const approvalLatest = ref(null)
/** 审批记录是否已加载完成 */
const approvalLoaded = ref(false)

const stationHeader = computed(() => {
  const s = stationRaw.value || {}
  const a = approvalLatest.value || {}
  const status = a.status
  const loaded = approvalLoaded.value
  return {
    id: route.query.stationId ?? s.stationId ?? s.id ?? '',
    name: form.projectName?.trim() || s.name || '—',
    model: s.productModel || a.productModel || '—',
    capacity: composeCapacity({ maxPower: s.maxPower, capacity: s.capacity })
      || composeCapacity({ maxPower: a.maxPower, capacity: a.capacity })
      || '—',
    region: s.area || a.area || '—',
    location: [s.province || a.province, s.city || a.city, s.district || a.district]
      .filter(Boolean).join('') || '—',
    applicant: a.applicantName || a.applicantAccount || '—',
    status: loaded && status != null ? (APPROVE_STATUS_TEXT[status] || '') : '',
    statusCode: loaded ? (status ?? null) : null,
    applyTime: fmtTime(a.applyTime) || '—',
  }
})

/** 已通过后不再允许编辑；奇点客服对已驳回电站也不显示编辑按钮 */
const canEdit = computed(() => {
  if (!approvalLoaded.value) return false
  const status = approvalLatest.value?.status
  if (status === 1) return false
  if (status === 2 && checkRole(['jd-support'])) return false
  return true
})

watch(
  () => route.query.step,
  (s) => {
    if (s == null || s === '') return
    const key = String(s).toLowerCase().trim()
    if (stepKeys.includes(key)) activeStep.value = key
  },
  { immediate: true },
)

watch(activeStep, (key) => {
  editingStepKey.value = null
  if (route.query.step === key) return
  router.replace({ path: route.path, query: { ...route.query, step: key } })
})

/** 加载最新一条审批记录（当前电站，按申请时间倒序取第一条） */
async function loadLatestApproval(stationId) {
  approvalLoaded.value = false
  try {
    const res = await getStationApprovalList({
      pageNum: 1,
      pageSize: 1,
      stationId: Number.isNaN(Number(stationId)) ? stationId : Number(stationId),
    })
    const rows = Array.isArray(res?.rows) ? res.rows : (res?.data?.rows || [])
    // 后端默认按 applyTime 或 createTime 倒序；这里再兜底一次
    const sorted = [...rows].sort((a, b) => {
      const ta = new Date(b.applyTime || b.createTime || 0).getTime()
      const tb = new Date(a.applyTime || a.createTime || 0).getTime()
      return ta - tb
    })
    approvalLatest.value = sorted[0] || null
  } catch (err) {
    console.error('获取审批记录失败:', err)
    approvalLatest.value = null
  } finally {
    approvalLoaded.value = true
  }
}

/** 加载电站基本信息并回填 form（与 station-add 保持一致的字段映射） */
async function loadStationDetail(stationId) {
  try {
    const res = await getStationDetail(stationId)
    const station = res?.data?.station || {}
    stationRaw.value = station
    form.projectName = station.name || ''
    form.projectCode = station.code || station.uuid || ''
    form.projectShortName = station.stationAbbreviation || station.shortName || ''
    form.maxpower = station.maxPower ?? station.maxpower ?? ''
    form.commossioningTime = station.commossioningTime || ''
    form.country = station.countryName || station.country || '中国'
    form.area = station.area || ''
    form.province = station.province || ''
    form.city = station.city || ''
    form.district = station.district || ''
    form.township = station.township || ''
    form.address = normalizeAddressDetail(station.address, {
      province: station.province,
      city: station.city,
      district: station.district,
      township: station.township,
    })
    form.investorEpc = station.customer || ''
    form.ownerEnterprise = station.operatingUnit || ''
    form.channelFramework = station.devopsUnit || ''
    form.industryCategory = station.industryCategory || ''
    form.strategySwitch = !!station.strategySwitch
    form.deviceFlags = parseDeviceFlags(station.deviceFlag)
    form.businessMode = Number(station.businessModel) === 2 ? 'non_self_invest' : 'self_invest'
    form.operationModel = Number(station.operatingModel) === 2 ? 'emc' : 'self'
    form.electricityCategory = Number(station.classification) === 0 ? 'large_industrial' : 'industrial'
    form.voltage = station.voltage
    form.stationType = Number(station.energyStorageType) === 2 ? 'centralized' : 'distributed'
    form.billingMode = Number(station.billingMode) === 1 ? 'capacity'
      : Number(station.billingMode) === 0 ? 'unknown' : 'demand'
    form.capacity = station.capacity ?? ''
    form.stationState = station.stationState ?? 1
    form.meterModel = station.meterModel ?? ''
    form.sort = station.sort ?? ''
    const longitude = Number(station.longitude)
    const latitude = Number(station.latitude)
    form.longitude = Number.isFinite(longitude) ? longitude : null
    form.latitude = Number.isFinite(latitude) ? latitude : null
    setPhotoSlotFromStationPicture(station.stationPicture || station.station_picture)
    const remoteStrategyConfig = res?.data?.strategyConfig
    existedStrategyConfig.value =
      remoteStrategyConfig && typeof remoteStrategyConfig === 'object' ? remoteStrategyConfig : null
    const remoteConfigStep = Number(station.configStep)
    serverConfigStep.value = Number.isFinite(remoteConfigStep) && remoteConfigStep > 0 ? remoteConfigStep : 0
  } catch (err) {
    console.error('获取电站基本信息失败:', err)
    stationRaw.value = {}
    existedStrategyConfig.value = null
    serverConfigStep.value = 0
    resetForm()
  }
}

/** 拉取电站拓扑并写入 draft，后续 topo 步骤挂载时由 watch 自动恢复到画布 */
async function loadStationTopology(stationId) {
  try {
    const res = await getTopology(stationId)
    const topoData = res?.data?.stationTopology
    const json = topoData ? JSON.parse(topoData) : null
    stationTopologyDraft.value = json && Object.keys(json).length ? json : null
  } catch (err) {
    console.error('获取电站拓扑失败:', err)
    stationTopologyDraft.value = null
  }
}

watch(
  () => route.query.stationId,
  (sid) => {
    if (sid == null || sid === '') {
      resetForm()
      stationRaw.value = {}
      approvalLatest.value = null
      approvalLoaded.value = false
      existedStrategyConfig.value = null
      serverConfigStep.value = 0
      stationTopologyDraft.value = null
      return
    }
    loadStationDetail(sid)
    loadLatestApproval(sid)
    loadStationTopology(sid)
  },
  { immediate: true },
)

/** 拓扑步骤 remount 或拓扑草稿变化后恢复到画布（只读/编辑切换、异步加载） */
watch(
  () => [activeStep.value, isStepEditing.value, topologyStepRef.value, stationTopologyDraft.value],
  async ([step, , ref, data]) => {
    if (step !== 'topo') return
    await nextTick()
    if (!ref?.loadTopologyJson || !data) return
    await ref.loadTopologyJson(data)
  },
  { flush: 'post' },
)

function startEdit() {
  editingStepKey.value = activeStep.value
}

function cancelEdit() {
  editingStepKey.value = null
  eui.showToast('已取消编辑', 'info')
}

/** 从当前 form + 详情缓存构造电站更新 payload
 *  targetStep: 保存当前步骤后期望达到的 configStep（basic=1 / device=2 / topo=3）
 *  与 station-add 保持一致：Math.max(serverConfigStep, targetStep) 单调推进，永不倒退
 */
function composeStationUpdatePayload(overrides = {}, targetStep = 0) {
  const { station, strategyConfig } = buildBasicPayload()
  station.stationPicture = getPhotoUrl()
  const stationId = stationHeader.value.id
  station.stationId = Number.isNaN(Number(stationId)) ? stationId : Number(stationId)
  const currentConfigStep = Number(serverConfigStep.value) || 0
  const finalStep = Math.max(currentConfigStep, Number(targetStep) || 0)
  station.configStep = finalStep
  Object.assign(station, overrides)
  const payloadStrategyConfig = existedStrategyConfig.value
    ? { ...strategyConfig, ...existedStrategyConfig.value }
    : strategyConfig
  return { payload: { station, strategyConfig: payloadStrategyConfig }, finalStep }
}

async function saveEdit() {
  const stationId = stationHeader.value.id
  if (stationId == null || stationId === '') {
    eui.showToast('未找到电站 ID,无法保存', 'error')
    return
  }

  if (activeStep.value === 'basic') {
    if (!validate()) return
    savingEdit.value = true
    try {
      const { payload, finalStep } = composeStationUpdatePayload({}, 1)
      await updateStation(payload)
      serverConfigStep.value = finalStep
      // 保存成功后刷新电站详情缓存,保证只读态展示最新数据
      await loadStationDetail(stationId)
      editingStepKey.value = null
      eui.showToast('基本信息已保存', 'success')
    } catch (err) {
      console.error('保存电站基本信息失败:', err)
      eui.showToast('保存失败,请重试', 'error')
    } finally {
      savingEdit.value = false
    }
    return
  }

  if (activeStep.value === 'device') {
    // 与 station-add 设备接入下一步保持一致：必须至少有一套储能系统(eLink)才能保存
    if (!deviceStepRef.value?.hasEnergyStorage?.()) {
      eui.showToast('请先添加储能系统后再继续', 'error')
      return
    }
    savingEdit.value = true
    try {
      const { payload, finalStep } = composeStationUpdatePayload({}, 2)
      await updateStation(payload)
      serverConfigStep.value = finalStep
      await loadStationDetail(stationId)
      editingStepKey.value = null
      eui.showToast('设备信息已保存', 'success')
    } catch (err) {
      console.error('保存电站设备信息失败:', err)
      eui.showToast('保存失败,请重试', 'error')
    } finally {
      savingEdit.value = false
    }
    return
  }

  if (activeStep.value === 'topo') {
    const liveTopo = topologyStepRef.value?.getTopologyJson?.()
    if (!liveTopo?.nodes?.length) {
      eui.showToast('当前拓扑为空,无法保存', 'info')
      return
    }
    stationTopologyDraft.value = liveTopo
    savingEdit.value = true
    try {
      await postTopology({
        stationId: Number.isNaN(Number(stationId)) ? stationId : Number(stationId),
        stationTopology: JSON.stringify(liveTopo),
      })
      // 拓扑非空时把 configStep 推进到 3（与 station-add 保存拓扑保持一致）
      const targetStep = liveTopo?.nodes?.length > 0 ? 3 : Number(serverConfigStep.value) || 0
      if (targetStep > (Number(serverConfigStep.value) || 0)) {
        const { payload, finalStep } = composeStationUpdatePayload({}, targetStep)
        await updateStation(payload)
        serverConfigStep.value = finalStep
      }
      editingStepKey.value = null
      eui.showToast('电站拓扑已保存', 'success')
    } catch (err) {
      console.error('保存电站拓扑失败:', err)
      eui.showToast('保存失败,请重试', 'error')
    } finally {
      savingEdit.value = false
    }
    return
  }

  editingStepKey.value = null
}

async function collectTopologyDraft() {
  const liveTopo = topologyStepRef.value?.getTopologyJson?.()
  if (liveTopo?.nodes?.length) stationTopologyDraft.value = liveTopo
}

function openRejectDialog() {
  if (editingStepKey.value) {
    eui.showToast('请先保存或取消当前步骤的编辑', 'info')
    return
  }
  if (!approvalLatest.value?.id) {
    eui.showToast('未找到审批记录,无法驳回', 'error')
    return
  }
  rejectDialogVisible.value = true
}

async function onRejectConfirm(reason) {
  const approvalId = approvalLatest.value?.id
  if (approvalId == null) {
    eui.showToast('未找到审批记录,无法驳回', 'error')
    return
  }
  rejectSubmitting.value = true
  try {
    await approveStationApproval({
      id: approvalId,
      pass: false,
      remark: reason,
    })
    rejectDialogVisible.value = false
    eui.showToast('已驳回审批申请', 'success')
    const sid = stationHeader.value.id
    if (sid != null && sid !== '') await loadLatestApproval(sid)
  } catch (err) {
    console.error('驳回审批失败:', err)
    eui.showToast('驳回失败,请重试', 'error')
  } finally {
    rejectSubmitting.value = false
  }
}

function openApproveDialog() {
  if (editingStepKey.value) {
    eui.showToast('请先保存或取消当前步骤的编辑', 'info')
    return
  }
  if (!approvalLatest.value?.id) {
    eui.showToast('未找到审批记录,无法投运', 'error')
    return
  }
  if (!validate()) {
    activeStep.value = 'basic'
    return
  }
  // 与 station-add 保持一致：投运前确认配置进度达到设备+拓扑
  const step = Number(serverConfigStep.value) || 0
  if (step < 2) {
    eui.showToast('该电站尚未添加储能系统,无法投运', 'error')
    activeStep.value = 'device'
    return
  }
  // 当前处于设备步骤时,以 DOM 中最新数据做一次强校验
  if (activeStep.value === 'device' && !deviceStepRef.value?.hasEnergyStorage?.()) {
    eui.showToast('请先添加储能系统后再投运', 'error')
    return
  }
  const hasTopoNodes = (stationTopologyDraft.value?.nodes?.length ?? 0) > 0
  if (step < 3 || !hasTopoNodes) {
    eui.showToast('该电站尚未完成拓扑绘制,无法投运', 'error')
    activeStep.value = 'topo'
    return
  }
  approveDialogVisible.value = true
}

async function onApproveConfirm() {
  const approvalId = approvalLatest.value?.id
  if (approvalId == null) {
    eui.showToast('未找到审批记录,无法投运', 'error')
    return
  }
  approveSubmitting.value = true
  try {
    await collectTopologyDraft()
    buildBasicPayload()
    await approveStationApproval({
      id: approvalId,
      pass: true,
      remark: '',
    })
    approveDialogVisible.value = false
    router.push({
      path: '/approve/success',
      query: {
        name: stationHeader.value.name,
        id: String(stationHeader.value.id ?? ''),
      },
    })
  } catch (err) {
    console.error('投运审批失败:', err)
    eui.showToast('投运失败,请重试', 'error')
  } finally {
    approveSubmitting.value = false
  }
}

/** 申请者视角：已驳回电站重新发起审批 */
async function onResubmit() {
  if (editingStepKey.value) {
    eui.showToast('请先保存或取消当前步骤的编辑', 'info')
    return
  }
  const stationId = stationHeader.value.id
  if (stationId == null || stationId === '') {
    eui.showToast('未找到电站 ID,无法提交审批', 'error')
    return
  }
  // 与 station-add 提交审批一致：储能系统 & 拓扑必须完成后才能提交
  if (!validate()) {
    activeStep.value = 'basic'
    return
  }
  const step = Number(serverConfigStep.value) || 0
  if (step < 2) {
    eui.showToast('请先添加储能系统后再提交审批', 'error')
    activeStep.value = 'device'
    return
  }
  if (activeStep.value === 'device' && !deviceStepRef.value?.hasEnergyStorage?.()) {
    eui.showToast('请先添加储能系统后再提交审批', 'error')
    return
  }
  const hasTopoNodes = (stationTopologyDraft.value?.nodes?.length ?? 0) > 0
  if (step < 3 || !hasTopoNodes) {
    eui.showToast('请先完成电站拓扑后再提交审批', 'error')
    activeStep.value = 'topo'
    return
  }
  resubmitting.value = true
  try {
    await submitStationApproval({
      stationId: Number.isNaN(Number(stationId)) ? stationId : Number(stationId),
    })
    eui.showToast('已重新提交审批', 'success')
    await loadLatestApproval(stationId)
  } catch (err) {
    console.error('重新提交审批失败:', err)
    eui.showToast('提交失败,请重试', 'error')
  } finally {
    resubmitting.value = false
  }
}
</script>

<style scoped lang="scss">
.approve-page {
  box-sizing: border-box;
  overflow: hidden;
}

.approve-body {
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.approve-frame {
  display: grid;
  grid-template-columns: minmax(240px, 280px) minmax(0, 1fr);
  align-items: stretch;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(8, 40, 87, 0.05);
}

.approve-sidebar {
  display: flex;
  flex-direction: column;
  padding: 24px 20px 20px;
  background: linear-gradient(187deg, rgba(188, 201, 250, 0.22) 0%, rgba(255, 255, 255, 0.55) 42%, #fff 100%);
  border-right: 1px solid var(--color-td-border);
  min-height: 0;
}

.approve-sidebar__hint {
  padding: 12px 14px;
  background: color-mix(in srgb, var(--color-primary) 6%, #f2f5fe);
  border: 1px solid color-mix(in srgb, var(--color-primary) 12%, var(--color-td-border));
  border-radius: 8px;
  color: var(--color-text);
  opacity: 0.92;
  line-height: 1.5;
}

.approve-sidebar__stepper {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding-right: 4px;
}

.approve-mobile-head {
  display: none;
  padding: 16px 16px 12px;
  border-bottom: 1px solid var(--color-td-border);
  background: #fff;
}

.approve-mobile-progress {
  height: 6px;
  border-radius: 999px;
  background: var(--color-th-border);
  overflow: hidden;
}

.approve-mobile-progress__fill {
  height: 100%;
  border-radius: inherit;
  background: var(--color-primary);
  transition: width 0.25s ease;
}

.approve-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: #fff;
}

.approve-step-body {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: rgba(255, 255, 255, 0.96);
  padding: 20px clamp(16px, 2.5vw, 28px) 20px;
}

.approve-step-body--device,
.approve-step-body--topo,
.approve-step-body--strategy {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.approve-step-body--device :deep(.step-device-access),
.approve-step-body--topo :deep(.step-topology),
.approve-step-body--strategy :deep(.step-strategy) {
  flex: 1;
  min-height: 0;
}

.approve-step-body--readonly {
  position: relative;

  :deep(.v-field--variant-outlined .v-field__outline) {
    --v-field-border-opacity: 0.28;
  }

  :deep(.v-input),
  :deep(.v-checkbox),
  :deep(.v-select) {
    opacity: 1;
  }

  :deep(.v-field--disabled),
  :deep(.v-input--disabled),
  :deep(.v-input--disabled .v-field),
  :deep(.v-input--disabled .v-field__field),
  :deep(.v-input--disabled .v-field__input),
  :deep(.v-input--disabled .v-label),
  :deep(.v-input--disabled .v-field-label) {
    opacity: 0.8 !important;
    --v-disabled-opacity: 0.8 !important;
  }

  /* 去掉字段内部的背景/hover 蒙层 */
  :deep(.v-field__overlay),
  :deep(.v-field__loader) {
    opacity: 0 !important;
  }

  :deep(.v-field__input),
  :deep(.v-field input),
  :deep(.v-field textarea),
  :deep(.v-select__selection-text),
  :deep(.v-autocomplete__selection-text) {
    color: #000 !important;
    -webkit-text-fill-color: #000 !important;
    opacity: 1 !important;
    font-weight: 400 !important;
  }

  /* 只读态下所有值文本一律使用常规字重，避免视觉上被误判为加粗 */
  :deep(.v-field),
  :deep(.v-field__field),
  :deep(.v-field__input),
  :deep(.v-field input),
  :deep(.v-field textarea),
  :deep(.v-select),
  :deep(.v-select__selection),
  :deep(.v-select__selection-text),
  :deep(.v-autocomplete),
  :deep(.v-autocomplete__selection),
  :deep(.v-autocomplete__selection-text),
  :deep(.v-combobox),
  :deep(.v-combobox__selection),
  :deep(.v-combobox__selection-text) {
    font-weight: 300 !important;
  }

  :deep(.v-label),
  :deep(.v-field-label) {
    color: #000 !important;
    opacity: 1 !important;
  }

  :deep(.v-btn),
  :deep(.v-tab),
  :deep(.v-selection-control),
  :deep(button),
  :deep(a),
  :deep(input),
  :deep(textarea),
  :deep(select),
  :deep(.photo-slot),
  :deep(.map-panel) {
    pointer-events: none;
    cursor: default !important;
  }

  /* 设备步骤：允许侧栏导航、储能方阵展开、复制 UUID/设备 ID 与主区域滚动 */
  :deep(.device-nav-item),
  :deep(.elink-card-head-main),
  :deep(.device-clipboard-btn),
  :deep(.elink-meta-value--copyable) {
    pointer-events: auto;
    cursor: pointer !important;
  }

  :deep(.elink-meta-value--copyable) {
    cursor: text !important;
  }

  :deep(.device-main),
  :deep(.strategy-main),
  :deep(.stp-canvas-wrap),
  :deep(.stp-topo-host) {
    pointer-events: auto;
  }

  /* 策略步骤：允许切换 Tab 查看（不可改表单项） */
  &.approve-step-body--strategy :deep(.v-tab) {
    pointer-events: auto;
    cursor: pointer !important;
  }
}

/* 只读 + 设备/拓扑/策略：内部子区域自行滚动 */
.approve-step-body--readonly.approve-step-body--device,
.approve-step-body--readonly.approve-step-body--topo,
.approve-step-body--readonly.approve-step-body--strategy {
  overflow: hidden;
}

.approve-step-body--readonly :deep(.form-line--device-flags .v-field__input),
.approve-step-body--readonly :deep(.form-line--device-flags input) {
  color: #000 !important;
  -webkit-text-fill-color: #000 !important;
  opacity: 1 !important;
  font-weight: 400 !important;
}

@media (max-width: 1279px) {
  .approve-frame {
    grid-template-columns: 1fr;
  }

  .approve-sidebar {
    display: none !important;
  }

  .approve-mobile-head {
    display: flex !important;
  }

  .approve-main {
    padding-top: 12px;
  }
}

@media (max-width: 599px) {
  .approve-sidebar__hint {
    padding: 10px 12px;
    font-size: 0.8125rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .approve-mobile-progress__fill {
    transition: none;
  }
}
</style>
